import { supabase, handleSupabaseError } from '@/lib/supabase';
import { UserRole } from '@/types/enums';
import { RbacAction, RBAC_RESOURCES, permKey } from '@/lib/rbac';

/**
 * Layanan RBAC: baca/tulis matriks `role_permissions` & `custom_roles`.
 * Penulisan hanya berhasil untuk Super Admin (RLS rp_manage / cr_manage).
 */

export interface PermissionRow {
  role: string;
  resource: string;
  action: string;
  allowed: boolean;
}

export interface PermissionChange {
  role: UserRole | string;
  resource: string;
  action: RbacAction;
  allowed: boolean;
}

export interface CustomRole {
  role_name: string;
  display_name: string;
  description?: string;
  is_active: boolean;
  created_at: string;
}

export interface CreateCustomRoleInput {
  role_name: string;
  display_name: string;
  description?: string;
}

export const rbacService = {
  /** Muat seluruh matriks → map key (`role.resource.action`) → allowed. */
  async getMatrix(): Promise<Record<string, boolean>> {
    const { data, error } = await supabase
      .from('role_permissions')
      .select('role, resource, action, allowed');
    if (error) throw new Error(handleSupabaseError(error));

    const map: Record<string, boolean> = {};
    for (const row of (data ?? []) as PermissionRow[]) {
      map[permKey(row.role, row.resource, row.action as RbacAction)] = row.allowed;
    }
    return map;
  },

  /**
   * Muat SELURUH baris permission untuk SATU role → Map `resource.action` → allowed.
   *
   * Berbeda dengan getRoleAllowedSet (hanya yang true), Map ini menyimpan nilai
   * eksplisit true MAUPUN false sehingga pemanggil bisa membedakan
   * "ditolak admin" (ada, false) dari "belum di-seed" (tidak ada → pakai default).
   */
  async getRoleMatrix(role: UserRole | string): Promise<Map<string, boolean>> {
    const { data, error } = await supabase
      .from('role_permissions')
      .select('resource, action, allowed')
      .eq('role', role);
    if (error) throw new Error(handleSupabaseError(error));
    const map = new Map<string, boolean>();
    for (const row of (data ?? []) as Pick<PermissionRow, 'resource' | 'action' | 'allowed'>[]) {
      map.set(`${row.resource}.${row.action}`, row.allowed);
    }
    return map;
  },

  /** Muat permission untuk SATU role → Set berisi `resource.action` yang diizinkan. */
  async getRoleAllowedSet(role: UserRole | string): Promise<Set<string>> {
    const { data, error } = await supabase
      .from('role_permissions')
      .select('resource, action, allowed')
      .eq('role', role)
      .eq('allowed', true);
    if (error) throw new Error(handleSupabaseError(error));
    const set = new Set<string>();
    for (const row of (data ?? []) as Pick<PermissionRow, 'resource' | 'action' | 'allowed'>[]) {
      set.add(`${row.resource}.${row.action}`);
    }
    return set;
  },

  /** Simpan perubahan (upsert batch). */
  async saveChanges(changes: PermissionChange[], updatedBy?: string): Promise<void> {
    if (changes.length === 0) return;
    const rows = changes.map((c) => ({
      role: c.role,
      resource: c.resource,
      action: c.action,
      allowed: c.allowed,
      updated_at: new Date().toISOString(),
      updated_by: updatedBy ?? null,
    }));
    const { error } = await supabase
      .from('role_permissions')
      .upsert(rows, { onConflict: 'role,resource,action' });
    if (error) throw new Error(handleSupabaseError(error));
  },

  /**
   * Catat setiap perubahan RBAC ke audit_logs.
   * Membutuhkan policy INSERT audit_insert_sa (migrasi 0011).
   */
  async logAuditChanges(
    changes: PermissionChange[],
    baseValues: Record<string, boolean>,
    changedBy: string,
  ): Promise<void> {
    if (changes.length === 0) return;
    const rows = changes.map((c) => {
      const key = permKey(c.role, c.resource, c.action);
      return {
        table_name: 'role_permissions',
        record_id: key,
        action: 'UPDATE' as const,
        old_data: { allowed: baseValues[key] ?? null, role: c.role, resource: c.resource, action: c.action },
        new_data: { allowed: c.allowed, role: c.role, resource: c.resource, action: c.action },
        changed_by: changedBy,
      };
    });
    const { error } = await supabase.from('audit_logs').insert(rows);
    if (error) {
      console.error('Gagal menulis audit_logs RBAC:', error.message);
    }
  },

  // ── Custom Roles ──────────────────────────────────────────────────────────

  /** Muat semua role kustom yang dibuat Super Admin. */
  async getCustomRoles(): Promise<CustomRole[]> {
    const { data, error } = await supabase
      .from('custom_roles')
      .select('role_name, display_name, description, is_active, created_at')
      .order('created_at', { ascending: true });
    if (error) throw new Error(handleSupabaseError(error));
    return (data ?? []) as CustomRole[];
  },

  /**
   * Buat role kustom baru dan seed seluruh baris role_permissions (semua false).
   * role_name dikonversi ke UPPER_SNAKE_CASE.
   */
  async createCustomRole(input: CreateCustomRoleInput, createdBy?: string): Promise<string> {
    const roleName = input.role_name
      .trim()
      .toUpperCase()
      .replace(/\s+/g, '_')
      .replace(/[^A-Z0-9_]/g, '');

    if (!roleName) throw new Error('Nama role tidak valid');

    const { error: insertErr } = await supabase.from('custom_roles').insert({
      role_name: roleName,
      display_name: input.display_name.trim(),
      description: input.description?.trim() || null,
      created_by: createdBy ?? null,
    });
    if (insertErr) throw new Error(handleSupabaseError(insertErr));

    // Seed role_permissions: semua resource × aksi = false (default tertutup).
    const seedRows = RBAC_RESOURCES.flatMap((res) =>
      res.actions.map((action) => ({
        role: roleName,
        resource: res.key,
        action,
        allowed: false,
        updated_by: createdBy ?? null,
      })),
    );
    const { error: seedErr } = await supabase
      .from('role_permissions')
      .upsert(seedRows, { onConflict: 'role,resource,action' });
    if (seedErr) throw new Error(handleSupabaseError(seedErr));

    return roleName;
  },

  /**
   * Hapus role kustom beserta seluruh permission-nya.
   * Built-in roles tidak boleh dihapus — validasi dilakukan di frontend.
   */
  async deleteCustomRole(roleName: string): Promise<void> {
    const { error: permErr } = await supabase
      .from('role_permissions')
      .delete()
      .eq('role', roleName);
    if (permErr) throw new Error(handleSupabaseError(permErr));

    const { error } = await supabase
      .from('custom_roles')
      .delete()
      .eq('role_name', roleName);
    if (error) throw new Error(handleSupabaseError(error));
  },
};

export default rbacService;
